import {
  Brand,
  Invoice,
  InvoiceLine,
  Item,
  ItemSupplierVariant,
  LineDataBySupplier,
  SalesTax,
  Unit,
} from '$models'
import { InvoiceExtended } from './invoiceExtended'
import { transformNumberProperty } from '$utils'
import { Box } from '@mui/material'
import { FieldValues, UseFormGetValues, UseFormSetValue } from 'react-hook-form'
import { isDayjs } from 'dayjs'
import { Dictionary } from 'lodash'

/**
 * Recalculates and updates the footer totals
 * @param getValues The forms getValues method
 * @param setValue The forms setValue method
 */
export const updateFooterTotals = (
  getValues: UseFormGetValues<FieldValues>,
  setValue: UseFormSetValue<FieldValues>
) => {
  const { subtotal, taxTotal, total } = calculateFooterTotals(
    getValues('invoice.lines'),
    getValues('invoice.unitCostIncludesTax')
  )
  setValue('invoice.calculatedSubtotal', subtotal)
  setValue('invoice.calculatedTaxTotal', taxTotal)
  setValue('invoice.calculatedTotal', total)
}

/**
 * Transforms a Invoice object to an InvoiceForm Object
 * @param invoice the Invoice object to transform
 * @returns
 */
export const transformInvoiceToInvoiceExtended = (
  invoice: Invoice
): InvoiceExtended => {
  const { subtotal, taxTotal, total } = calculateFooterTotals(
    invoice.lines,
    invoice.unitCostIncludesTax
  )

  return {
    ...invoice,
    calculatedSubtotal: subtotal,
    calculatedTaxTotal: taxTotal,
    calculatedTotal: total,
    _supplierName: invoice.supplierName,
  } as InvoiceExtended
}

/**
 * Gets the default brand and ordering unit of an item for a given row and item
 * @param item the item object the user has selected
 * @param supplierId the supplierId of the invoice
 * @param brands all possible brands
 * @param units all possible units
 * @returns object with properties brandId, brandName, unitId, unitName that are undefined or string
 */
export const getDefaultValuesFromItem = (
  item: Item | undefined,
  supplierId: string | undefined,
  brands: Dictionary<Brand>,
  units: Dictionary<Unit>
) => {
  const result: {
    code?: string
    brandId?: string
    brandName?: string
    unitId?: string
    unitName?: string
  } = {
    code: undefined,
    brandId: undefined,
    brandName: undefined,
    unitId: undefined,
    unitName: undefined,
  }

  const itemsForSupplier = item?.suppliers?.filter(
    (i) => i.supplierId == supplierId
  )

  // get the default item per supplier using defaultForSupplier if is there any.
  let defaultItemForSupplier: ItemSupplierVariant | undefined = undefined
  if (itemsForSupplier && itemsForSupplier.length) {
    defaultItemForSupplier =
      itemsForSupplier.find(
        (itemSupplierVariant) => itemSupplierVariant.defaultForSupplier
      ) || itemsForSupplier[0]
  }

  result.code = defaultItemForSupplier?.stockCode
  const defaultBrandId =
    defaultItemForSupplier?.brandId ?? item?.defaultBrandId ?? ''
  const defaultBrandName = brands[defaultBrandId]?.name

  // Only set the brand defaults if we can find the brand otherwise we assume the brand was been deleted
  // thus do not set it
  if (defaultBrandName) {
    result.brandName = defaultBrandName
    result.brandId = defaultBrandId
  }
  const defaultOrderingUnitId = item?.orderingUnitId ?? ''
  const defaultOrderingUnitName = units[defaultOrderingUnitId]?.name

  // Only set the unit defaults if we can find the unit otherwise we assume the brand was been deleted
  // thus do not set it
  if (defaultOrderingUnitName) {
    result.unitName = defaultOrderingUnitName
    result.unitId = defaultOrderingUnitId
  }

  return result
}

/**
 * Valitdates the an Invoice Form and returns errors in a Set
 * @param invoiceForm the values of the invoice form to be validated
 * @returns A Set<string> of all errors
 */
export const validateInvoice = (
  invoiceForm: InvoiceExtended | null
): Set<string> => {
  const errors: Set<string> = new Set<string>()

  if (!invoiceForm?.linkedSupplierId) {
    errors.add(
      'The supplier for this invoice is not currently in Loaded. Please change the supplier or create a new one.'
    )
  }

  for (const line of invoiceForm!.lines!) {
    // Skip deleted lines
    if (line.deletedAt) continue

    checkItemErrors(line, errors)
    checkBrandErrors(line, errors)
    checkUnitErrors(line, errors)
  }

  if (!invoiceForm?.receivedAt) {
    errors.add('Please enter a received date.')
  }

  if (
    invoiceForm?.calculatedTotal !==
    transformNumberProperty(invoiceForm?.total as unknown as string)
  ) {
    errors.add(
      'The invoice line totals do not match the invoice total. Please update the invoice lines or invoice total.'
    )
  }

  return errors
}

/**
 * Formats an Invoice object to be ready to be saved in the backend
 * @param invoice the invoice to bbe saved
 * @returns Invoice ready to be sent to the backend
 */
export const transformInvoiceOnSave = (invoice: Invoice): Invoice => {
  return new Invoice({
    ...invoice,
    dueAt: isDayjs(invoice.dueAt)
      ? invoice.dueAt.format('YYYY-MM-DD')
      : undefined,
    issuedAt: isDayjs(invoice.issuedAt)
      ? invoice.issuedAt.format('YYYY-MM-DD')
      : undefined,
    total:
      typeof invoice.total === 'string'
        ? transformNumberProperty(invoice.total)
        : invoice.total,
    lines: invoice.lines?.map((line) => {
      return new InvoiceLine({
        ...line,
        code: line.code === '' ? undefined : line.code,
        quantityReceived:
          typeof line.quantityReceived === 'string'
            ? line.quantityReceived === ''
              ? undefined
              : transformNumberProperty(line.quantityReceived)
            : line.quantityReceived,

        unitCost:
          typeof line.unitCost === 'string'
            ? line.unitCost === ''
              ? undefined
              : transformNumberProperty(line.unitCost)
            : line.unitCost,
        totalCost:
          typeof line.totalCost === 'string'
            ? line.totalCost === ''
              ? undefined
              : transformNumberProperty(line.totalCost)
            : line.totalCost,
      })
    }),
  })
}

/**
 * Rounds a value and returns the rounded number as a string
 * @param value the value to be rounded
 * @param dp the decimal places to round the value to
 * @returns rounded number as a string or undefined
 */
export const roundValue = (
  value: number | undefined,
  dp: 2 | 3
): string | undefined => {
  // Round to given decimal places and make sure there are always the given decimal places
  return value != undefined
    ? (Math.round(value * 10 ** dp) / 10 ** dp).toFixed(dp)
    : undefined
}

/**
 * Handles retrieving a lines saleTaxRate for calculations
 * (Takes into account if saleTaxRate is undefined and returns 0 if so)
 * @param formPrefix the name of the form's invoice line
 * @returns the invoice lines sale tax rate or 0 if undefined
 */
export const getSaleTaxRateForCalculations = (
  formPrefix: string,
  getValues: UseFormGetValues<FieldValues>
) => getValues(`${formPrefix}.saleTaxRate`) ?? 0

/**
 * Swaps the characters at the given indexes inside the given string
 * @param stringValue the string to swap chars in
 * @param index1 index of char 1
 * @param index2 index of char 2
 * @returns the string with the chars swapped
 */
export const swapStringChars = (
  stringValue: string,
  index1: number,
  index2: number
): string => {
  const charArray: string[] = stringValue.split('')

  // Swap the characters at each index
  ;[charArray[index1], charArray[index2]] = [
    charArray[index2],
    charArray[index1],
  ]

  return charArray.join('')
}

/**
 * Produces a message to be shown in a tooltip explaining that the value
 * in an input is different than what it was on the original document.
 * @param fieldName
 * @param originalValue
 * @returns
 */
export function buildFieldAlteredTooltipMessage(
  fieldName: string,
  originalValue: string | undefined
) {
  return (
    <Box textAlign={'center'} p={1}>
      <p>This {fieldName} differs from what we found on the invoice.</p>
      <p>Recognised Value: {originalValue ?? 'Not Set'}</p>
    </Box>
  )
}

/**
 * Produces a message to be shown in a tooltip explaining that the value
 * matches a Loaded property (e.g. a supplier stock code) exactly.
 * @param fieldName
 * @param loadedPropertyName
 * @returns
 */
export function buildFieldMatchesTooltipMessage(
  fieldName: string,
  loadedPropertyName: string
) {
  return (
    <Box textAlign={'center'} p={1}>
      <p>
        This {fieldName} matches {loadedPropertyName} exactly.
      </p>
    </Box>
  )
}

/**
 * Produces a message to be shown in a tooltip explaining that the value
 * doesn't exist in Loaded currently.
 * @param fieldName
 * @param loadedPropertyName
 * @returns
 */
export function buildFieldIsNewTooltipMessage(fieldName: string) {
  return (
    <Box textAlign={'center'} p={1}>
      <p>This {fieldName} is not currently set up in this venue.</p>
    </Box>
  )
}

/**
 * Extracts all line data variants and puts them into a dictionary sorted by supplier ID
 * @param items all items in Loaded
 * @param brands all brands in Loaded
 * @param units all units in Loaded
 * @param saleTaxRates the company's sale tax rates
 * @returns
 */
export function extractLineDataVariants(
  items: Dictionary<Item>,
  brands: Dictionary<Brand>,
  units: Dictionary<Unit>,
  saleTaxRates: SalesTax[]
): LineDataBySupplier {
  return Object.values(items).reduce((accumulator, item) => {
    if (!item.suppliers || !item.name) return accumulator

    item.suppliers.forEach((itemSupplier) => {
      if (!itemSupplier.supplierId) return

      const brand = itemSupplier.brandId
        ? brands[itemSupplier.brandId]
        : undefined
      const unit = itemSupplier.unitId ? units[itemSupplier?.unitId] : undefined
      const saleTaxRate = extractSaleTaxRate(
        saleTaxRates,
        item.globalSalesTaxSortOrder
      )

      const variants = accumulator[itemSupplier.supplierId] ?? []

      accumulator[itemSupplier.supplierId] = [
        ...variants,
        {
          ...itemSupplier,
          itemName: item.name!,
          // Note: using the brand/unit ?.id here as if not brand or unit found then wan these to be undefined
          // Its possible for variants to be using deleted brands and/or units
          brandId: brand?.id,
          brandName: brand?.name,
          unitId: unit?.id,
          unitName: unit?.name,
          saleTaxRate,
          selectedSalesTax: item.globalSalesTaxSortOrder ?? -1,
        },
      ]
    })

    return accumulator
  }, {} as LineDataBySupplier)
}

/**
 * Extract the sale tax rate for an item
 * @param saleTaxRates The company's sale tax rates
 * @param globalSalesTaxSortOrder the globalSalesTaxSortOrder of the item being matched
 * @returns the saleTaxRate of the item, or undefined of not found
 */
export function extractSaleTaxRate(
  saleTaxRates: SalesTax[],
  globalSalesTaxSortOrder: number | undefined
): number | undefined {
  return saleTaxRates.find((rate) => rate.sortOrder === globalSalesTaxSortOrder)
    ?.rate
}

/**
 * Calculates the tax amount for a line
 * @param unitCost the unit cost of the line
 * @param quantityReceived the quanity received of the line
 * @param saleTaxRate the sale tax rate of the line
 * @returns the total tax amount for the line
 */
export function calculateTaxAmount(
  unitCost: number | undefined,
  quantityReceived: number | undefined,
  saleTaxRate: number | undefined
): number {
  return (unitCost ?? 0) * (quantityReceived ?? 0) * (saleTaxRate ?? 0)
}

/**
 * Calculates the totals of invoice lines and formats them ready for display
 * @param lines The Invoice lines
 * @param unitCostIncludesTax Whether or not unitCost's include tax or not (assumed false if undefined)
 * @returns object with subtotal, taxTotal, and total values
 */
const calculateFooterTotals = (
  lines: InvoiceLine[] | undefined,
  unitCostIncludesTax: boolean | undefined
): { subtotal: number; taxTotal: number; total: number } => {
  let summedSubtotal = 0
  let summedTax = 0
  let summedTotal = 0

  lines?.forEach((line) => {
    // Dont include deleted lines in the sums
    if (line.deletedAt != undefined) return

    const unitCost =
      typeof line.unitCost === 'string'
        ? transformNumberProperty(line.unitCost)
        : line.unitCost ?? 0
    const quantityReceived =
      typeof line.quantityReceived === 'string'
        ? transformNumberProperty(line.quantityReceived)
        : line.quantityReceived ?? 0
    const saleTaxRate =
      typeof line.saleTaxRate === 'string'
        ? transformNumberProperty(line.saleTaxRate)
        : line.saleTaxRate

    const currentSubtotal = unitCost * quantityReceived
    // If saleTaxRate is undefined then we assume there is no tax
    const currentTaxAmount = currentSubtotal * (saleTaxRate ?? 0)

    summedSubtotal += unitCostIncludesTax
      ? currentSubtotal - currentTaxAmount
      : currentSubtotal
    summedTax += currentTaxAmount
    summedTotal += unitCostIncludesTax
      ? currentSubtotal
      : currentSubtotal + currentTaxAmount
  })

  // We round to 2dp here to get rid of cases where the number is for example '.0000000003'
  // And we make the assumption that most user will use 2dp in the Invoice Total input
  return {
    subtotal: Math.round(summedSubtotal * 100) / 100,
    taxTotal: Math.round(summedTax * 100) / 100,
    total: Math.round(summedTotal * 100) / 100,
  }
}

/**
 * Checks a line for any item errors, will add an error to the errors set if so
 * @param line The line to check
 * @param errors The set or errors
 */
const checkItemErrors = (line: InvoiceLine, errors: Set<string>) => {
  const singleItemError =
    'There is a new item in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  const multipleItemErrors =
    'There are new items in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  if (!line.linkedItemId) {
    if (!errors.has(singleItemError) && !errors.has(multipleItemErrors)) {
      errors.add(singleItemError)
    } else {
      errors.delete(singleItemError)
      errors.add(multipleItemErrors)
    }
  }
}

/**
 * Checks a line for any brand errors, will add an error to the errors set if so
 * @param line The line to check
 * @param errors The set or errors
 */
const checkBrandErrors = (line: InvoiceLine, errors: Set<string>) => {
  const singleBrandError =
    'There is a new brand in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  const multipleBrandErrors =
    'There are new brands in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  // Brands are not mandatory thus are able to be undefined
  if (line.brand != null && line.brand != '' && !line.linkedBrandId) {
    if (!errors.has(singleBrandError) && !errors.has(multipleBrandErrors)) {
      errors.add(singleBrandError)
    } else {
      errors.delete(singleBrandError)
      errors.add(multipleBrandErrors)
    }
  }
}

/**
 * Checks a line for any unit errors, will add an errors to the errors set if so
 * @param line The line to check
 * @param errors The set or errors
 */
const checkUnitErrors = (line: InvoiceLine, errors: Set<string>) => {
  const singleUnitError =
    'There is a new unit in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  const multipleUnitErrors =
    'There are new units in your invoice lines which must be resolved. Please match to existing records or create a new record before receiving.'
  if (!line.linkedUnitId) {
    if (!errors.has(singleUnitError) && !errors.has(multipleUnitErrors)) {
      errors.add(singleUnitError)
    } else {
      errors.delete(singleUnitError)
      errors.add(multipleUnitErrors)
    }
  }
}
